/**
 * Contact sales, the paid-key intake for the air surfaces. aircargomcp / airratesmcp are metered paid
 * API products, so a prospect needs a way to ask for a quote and a production key. This posts to the
 * shared navo24 family intake (api.trackingmcp.com/v1/contact-sales), the same endpoint the sibling
 * products use, tagged with topic 'api' and this surface as the source. The request, its Turnstile token
 * and the mapping of the intake's answers live in lib/contact-sales.ts (SCRUM-61). On any failure the
 * inputs keep what was typed, the server's field errors show inline, and a mailto:sales@navo24.com link
 * is offered so a lead is never lost.
 *
 * Styling rides the navo24 token contract (card / btn-accent / mono / text-ink…), same as RateForm and
 * AircargoHome, so it inherits the light cream ground and flips theme for free.
 */

import { type FormEvent, useState } from 'react'
import { Layout } from '../components/Layout'
import {
  CONTACT_LIMITS,
  type ContactField,
  type ContactResult,
  submitContactSales,
} from '../lib/contact-sales'
import { PageMeta } from '../lib/meta'
import { SURFACE } from '../surface'

const SALES_EMAIL = 'sales@navo24.com'

// Which product this lead came from, per the intake contract ('aircargo' | 'airrates').
const SOURCE = SURFACE === 'airrates' ? 'airrates' : 'aircargo'

type FieldErrors = Partial<Record<ContactField, string>>

type State =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'done'; email: string }
  | { kind: 'error'; reason: Exclude<ContactResult, { ok: true }>['kind']; message: string }

const INPUT_CLASS =
  'mt-1.5 w-full rounded-xl border bg-surface-0 px-3.5 py-2.5 text-[15px] text-ink outline-none transition-colors placeholder:text-faint focus:border-signal disabled:opacity-60'

function FieldError({ id, text }: { id: string; text?: string | undefined }) {
  if (!text) return null
  return (
    <p id={id} className="mt-1.5 text-xs leading-relaxed text-down">
      {text}
    </p>
  )
}

export function ContactPage() {
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [state, setState] = useState<State>({ kind: 'idle' })
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const ready = emailValid && state.kind !== 'submitting'

  // Editing a field clears the server's complaint about it, so a fixed field stops looking wrong.
  function edit(field: ContactField, set: (v: string) => void) {
    return (v: string) => {
      set(v)
      if (fieldErrors[field]) setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const borderFor = (field: ContactField) => (fieldErrors[field] ? 'border-down' : 'border-border')

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!emailValid || state.kind === 'submitting') return
    setState({ kind: 'submitting' })
    setFieldErrors({})
    // Never throws. On any failure the inputs keep what the person typed: nothing here clears them.
    const result = await submitContactSales({ name, company, email, message, source: SOURCE })
    if (result.ok) {
      setState({ kind: 'done', email: email.trim() })
      return
    }
    setFieldErrors(result.fields)
    setState({ kind: 'error', reason: result.kind, message: result.message })
  }

  return (
    <Layout>
      <PageMeta
        title="Contact sales for a quote | AirCargoMCP"
        description="Talk to us about a production API key. Tell us your monthly volume and use-case, and the team will price a plan for your air-cargo tracking and rate needs."
        path="/contact"
      />
      <section className="mx-auto max-w-2xl px-5 pt-14 pb-8">
        <span className="eyebrow">Talk to sales</span>
        <h1 className="mt-4 text-[clamp(2rem,4.6vw,2.9rem)] font-light tracking-[-0.03em] text-ink">
          Contact sales for a <span className="hl">quote</span>.
        </h1>
        <p className="mt-4 max-w-xl text-[16px] leading-[1.55] text-muted">
          The API and MCP surface is a metered, paid product. Tell us your expected monthly volume
          and what you are building, and the team will price a plan and issue a production key.
        </p>

        {state.kind === 'done' ? (
          <div className="reveal card mt-8 border-l-2 border-l-signal p-6">
            <div className="text-lg font-semibold text-ink">Thanks, we have your request.</div>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              The team will reply to <span className="mono text-ink">{state.email}</span> shortly,
              with pricing and next steps.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="card mt-8 p-6 sm:p-7">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="min-w-0">
                <label
                  htmlFor="cs-name"
                  className="mono text-[11px] uppercase tracking-wider text-faint"
                >
                  Name
                </label>
                <input
                  id="cs-name"
                  value={name}
                  onChange={(e) => edit('name', setName)(e.target.value)}
                  autoComplete="name"
                  maxLength={CONTACT_LIMITS.name}
                  aria-invalid={fieldErrors.name ? true : undefined}
                  aria-describedby={fieldErrors.name ? 'cs-name-error' : undefined}
                  disabled={state.kind === 'submitting'}
                  className={`${INPUT_CLASS} ${borderFor('name')}`}
                  placeholder="Jane Rivera"
                />
                <FieldError id="cs-name-error" text={fieldErrors.name} />
              </div>
              <div className="min-w-0">
                <label
                  htmlFor="cs-company"
                  className="mono text-[11px] uppercase tracking-wider text-faint"
                >
                  Company (optional)
                </label>
                <input
                  id="cs-company"
                  value={company}
                  onChange={(e) => edit('company', setCompany)(e.target.value)}
                  autoComplete="organization"
                  maxLength={CONTACT_LIMITS.company}
                  aria-invalid={fieldErrors.company ? true : undefined}
                  aria-describedby={fieldErrors.company ? 'cs-company-error' : undefined}
                  disabled={state.kind === 'submitting'}
                  className={`${INPUT_CLASS} ${borderFor('company')}`}
                  placeholder="Acme Forwarding"
                />
                <FieldError id="cs-company-error" text={fieldErrors.company} />
              </div>
            </div>

            <div className="mt-5 min-w-0">
              <label
                htmlFor="cs-email"
                className="mono text-[11px] uppercase tracking-wider text-faint"
              >
                Work email <span className="text-down">required</span>
              </label>
              <input
                id="cs-email"
                type="email"
                required
                value={email}
                onChange={(e) => edit('email', setEmail)(e.target.value)}
                autoComplete="email"
                maxLength={CONTACT_LIMITS.email}
                aria-invalid={fieldErrors.email ? true : undefined}
                aria-describedby={fieldErrors.email ? 'cs-email-error' : undefined}
                disabled={state.kind === 'submitting'}
                className={`${INPUT_CLASS} ${borderFor('email')}`}
                placeholder="jane@acmeforwarding.com"
              />
              <FieldError id="cs-email-error" text={fieldErrors.email} />
            </div>

            <div className="mt-5 min-w-0">
              <label
                htmlFor="cs-message"
                className="mono text-[11px] uppercase tracking-wider text-faint"
              >
                What do you need?
              </label>
              <textarea
                id="cs-message"
                value={message}
                onChange={(e) => edit('message', setMessage)(e.target.value)}
                rows={5}
                maxLength={CONTACT_LIMITS.message}
                aria-invalid={fieldErrors.message ? true : undefined}
                aria-describedby={fieldErrors.message ? 'cs-message-error' : undefined}
                disabled={state.kind === 'submitting'}
                className={`${INPUT_CLASS} resize-y leading-relaxed ${borderFor('message')}`}
                placeholder="Roughly how many AWB lookups or rate quotes per month, and what you are building (dashboard, agent, TMS integration)."
              />
              <FieldError id="cs-message-error" text={fieldErrors.message} />
            </div>

            <button
              type="submit"
              disabled={!ready}
              className="btn-accent mt-6 w-full rounded-xl px-6 py-3 text-center font-semibold disabled:cursor-not-allowed disabled:opacity-45"
            >
              {state.kind === 'submitting' ? 'Sending…' : 'Request a quote'}
            </button>

            {state.kind === 'error' && (
              <p role="alert" className="reveal mt-4 text-sm leading-relaxed text-muted">
                {state.message}
                {state.reason !== 'validation' && (
                  <>
                    {' '}
                    You can also email us directly at{' '}
                    <a
                      className="font-semibold text-signal hover:underline"
                      href={`mailto:${SALES_EMAIL}`}
                    >
                      {SALES_EMAIL}
                    </a>{' '}
                    and we will pick it up.
                  </>
                )}
              </p>
            )}

            <p className="mt-4 text-xs leading-relaxed text-faint">
              Prefer email? Reach the team any time at{' '}
              <a className="text-muted hover:text-ink" href={`mailto:${SALES_EMAIL}`}>
                {SALES_EMAIL}
              </a>
              .
            </p>
          </form>
        )}
      </section>
    </Layout>
  )
}
